import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import fileIcon from "../assets/images/common/ico_file.gif";

const Container = styled.div`
    width: 100%;
    margin-bottom: 30px;
`;

const Table = styled.table`
    width: 100%;
    font-size: 14px;
    border-collapse: collapse;
    margin-top: 15px;
    table-layout: fixed;
`;

const Thead = styled.thead`
    background: #f5f6f6;
`;

const Tr = styled.tr`
    border-bottom: 1px solid #e3e3e3;
    
    &.notice {
        background-color: #f9f9f9;
    }
    
    &:hover {
        background-color: #f9f9f9;
    }
`;

const Th = styled.th`
    height: 45px;
    color: #444;
    padding: 10px;
    text-align: center;
    font-weight: 600;
    border-top: 2px solid #666;
    border-bottom: 1px solid #ddd;
`;

const Td = styled.td`
    height: 45px;
    padding: 10px;
    color: #666;
    text-align: center;
    vertical-align: middle;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    &.subject {
        text-align: left;
    }
    
    &.notice-label {
        font-weight: bold;
        color: #ff6600;
    }
`;

const TitleWrapper = styled.span`
    display: block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    position: relative;
`;

const TitleLink = styled(Link)`
    color: #666;
    text-decoration: none;
    display: flex;
    align-items: center;
    
    &:hover {
        text-decoration: underline;
        color: #333;
    }
`;

const FileIcon = styled.img`
    display: inline-block;
    width: 15px;
    height: 15px;
    margin-left: 5px;
    vertical-align: middle;
    position: relative;
    z-index: 1;
`;

const EmptyMessage = styled.td`
    text-align: center;
    padding: 50px 0;
    color: #888;
`;

// 페이지네이션 스타일
const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 20px;
`;

const PageButton = styled.button`
    width: 35px;
    height: 35px;
    margin: 0 3px;
    border: 1px solid ${props => props.active ? '#999' : '#ddd'};
    background-color: ${props => props.active ? '#f5f5f5' : 'white'};
    color: ${props => props.active ? '#333' : '#666'};
    cursor: pointer;
    border-radius: 3px;
    font-size: 13px;
    
    &:hover {
        background-color: ${props => props.active ? '#f5f5f5' : '#f9f9f9'};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PageNavButton = styled(PageButton)`
    width: auto;
    padding: 0 10px;
`;

const WriteButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 20px 0;
`;

const WriteButton = styled(Link)`
    background-color: #007bff;
    color: white;
    padding: 10px 20px;
    font-size: 14px;
    border-radius: 4px;
    text-decoration: none;

    &:hover {
        background-color: #0056b3;
    }
`;

const BoardTable = ({
                        posts = [],
                        loading = false,
                        basePath = '',
                        currentPage = 1,
                        totalPages = 1,
                        onPageChange
                    }) => {
    const [isAdmin, setIsAdmin] = useState(false);
    const pathParts = basePath.split('/');
    const mainSection = pathParts[1]; // "news"
    const category = pathParts[2]; // "student-council"
    // 관리자 권한 체크
    useEffect(() => {
        const checkAdminStatus = () => {
            const userRole = localStorage.getItem('user_role') || sessionStorage.getItem('user_role');
            setIsAdmin(userRole === 'admin');
        };

        checkAdminStatus();

        // storage 이벤트 리스너로 권한 변경 감지
        const handleStorageChange = () => {
            checkAdminStatus();
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    if (loading) {
        return <div>로딩 중...</div>;
    }

    // 공지사항 게시글과 일반 게시글 분리
    const noticePosts = posts.filter(post => post.isNotice);
    const normalPosts = posts.filter(post => !post.isNotice);

    // 페이지네이션 생성
    const renderPagination = () => {
        const pages = [];

        // 페이지 범위 설정 (현재 페이지 주변 10개 페이지)
        let startPage = Math.max(1, currentPage - 4);
        let endPage = Math.min(totalPages, currentPage + 5);

        // 범위 조정
        if (endPage - startPage < 9 && totalPages > 10) {
            if (startPage === 1) {
                endPage = Math.min(10, totalPages);
            } else if (endPage === totalPages) {
                startPage = Math.max(1, totalPages - 9);
            }
        }

        // 처음 페이지 버튼
        pages.push(
            <PageNavButton
                key="first"
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
            >
                처음
            </PageNavButton>
        );

        // 이전 페이지 버튼
        pages.push(
            <PageNavButton
                key="prev"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                이전
            </PageNavButton>
        );

        // 페이지 번호 버튼
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <PageButton
                    key={i}
                    active={i === currentPage}
                    onClick={() => onPageChange(i)}
                >
                    {i}
                </PageButton>
            );
        }

        // 다음 페이지 버튼
        pages.push(
            <PageNavButton
                key="next"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                다음
            </PageNavButton>
        );

        // 마지막 페이지 버튼
        pages.push(
            <PageNavButton
                key="last"
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
            >
                마지막
            </PageNavButton>
        );

        return pages;
    };

    return (
        <Container>
            <Table>
                <Thead>
                    <Tr>
                        <Th width="8%">NO</Th>
                        <Th width="54%">제목</Th>
                        <Th width="14%">글쓴이</Th>
                        <Th width="15%">작성일</Th>
                        <Th width="10%">조회수</Th>
                    </Tr>
                </Thead>
                <tbody>
                {/* 공지사항 게시글 */}
                {noticePosts.map(post => (
                    <Tr key={`notice-${post.id}`} className="notice">
                        <Td className="notice-label">공지</Td>
                        <Td className="subject">
                            <TitleWrapper>
                                <TitleLink to={`${basePath}/detail/${post.id}`} title={post.title}>
                                    <span className="title-text" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {post.title}
                                    </span>
                                    {post.hasAttachment && (
                                        <FileIcon src={fileIcon} alt="첨부파일" />
                                    )}
                                </TitleLink>
                            </TitleWrapper>
                        </Td>
                        <Td>{post.author}</Td>
                        <Td style={{ fontSize: '13px' }}>{post.createdAt}</Td>
                        <Td>{post.views}</Td>
                    </Tr>
                ))}

                {/* 일반 게시글 */}
                {normalPosts.length > 0 ? (
                    normalPosts.map((post) => (
                        <Tr key={post.id}>
                            <Td>{post.displayNumber}</Td>
                            <Td className="subject">
                                <TitleWrapper>
                                    <TitleLink to={`${basePath}/detail/${post.id}`} title={post.title}>
                                    <span className="title-text" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {post.title}
                                    </span>
                                        {post.hasAttachment && (
                                            <FileIcon src={fileIcon} alt="첨부파일" />
                                        )}
                                    </TitleLink>
                                </TitleWrapper>
                            </Td>
                            <Td>{post.author}</Td>
                            <Td style={{ fontSize: '13px' }}>{post.createdAt}</Td>
                            <Td>{post.views}</Td>
                        </Tr>
                    ))
                ) : (
                    <Tr>
                        <EmptyMessage colSpan="5">
                            게시글이 없습니다.
                        </EmptyMessage>
                    </Tr>
                )}
                </tbody>
            </Table>

            <PaginationContainer>
                {renderPagination()}
            </PaginationContainer>

            {/* 관리자일 때만 글쓰기 버튼 표시 */}
            {isAdmin && (
                <WriteButtonContainer>
                    <WriteButton to={`${basePath}/write?board=${category}`}>글쓰기</WriteButton>
                </WriteButtonContainer>
            )}
        </Container>
    );
};

export default BoardTable;